import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../utils/api';
import './Status.css';

const Status = () => {
  const navigate = useNavigate();
  const { showError, showSuccess, showWarning } = useNotification();
  // user não utilizado diretamente; usamos hasPermission do AuthContext
  // const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [filterDomain, setFilterDomain] = useState('');
  const [filterActive, setFilterActive] = useState('');
  const [activeTab, setActiveTab] = useState('definitions');
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [overrides, setOverrides] = useState([]);
  const [loadingOverrides, setLoadingOverrides] = useState(false);
  const [overrideDomain, setOverrideDomain] = useState('');
  const [overrideHidden, setOverrideHidden] = useState('');
  const [newOverrideStatusId, setNewOverrideStatusId] = useState('');
  const [newOverrideOrder, setNewOverrideOrder] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [modalTab, setModalTab] = useState('status');
  const [showDeleteOverrideModal, setShowDeleteOverrideModal] = useState(false);
  const [overrideToDelete, setOverrideToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [statusToDelete, setStatusToDelete] = useState(null);
  const [formData, setFormData] = useState({
    domain: '',
    key: '',
    label: '',
    color: '#cccccc',
    icon: '',
    is_active: true,
    is_default: false
  });
  const [formErrors, setFormErrors] = useState({});
  const [canManageStatus, setCanManageStatus] = useState(false);
  const [selectedProjectsForStatus, setSelectedProjectsForStatus] = useState([]);
  const [projectsAction, setProjectsAction] = useState('visible');
  const [projectOverrideOrder, setProjectOverrideOrder] = useState(0);
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [prefillError, setPrefillError] = useState('');
  const [projectsSearch, setProjectsSearch] = useState('');

  const fetchStatuses = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filterDomain) params.append('domain', filterDomain);
      if (filterActive) params.append('active', filterActive);

      const response = await fetch(getApiUrl(`status?${params}`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStatuses(data.data);
      } else {
        showError('Erro ao carregar status');
      }
    } catch (error) {
      console.error('Erro ao buscar status:', error);
      showError('Erro ao carregar status');
    } finally {
      setLoading(false);
    }
  }, [filterDomain, filterActive, showError]);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  const fetchProjects = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('projects'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Compatibilidade com diferentes formatos de resposta do backend
        // Pode vir como { success, projects } ou { success, data } ou array direto
        const list = Array.isArray(data)
          ? data
          : (Array.isArray(data.projects)
            ? data.projects
            : (Array.isArray(data.data)
              ? data.data
              : []));
        setProjects(list);
      } else {
        showError('Erro ao carregar projetos');
      }
    } catch (error) {
      console.error('Erro ao buscar projetos:', error);
      showError('Erro ao carregar projetos');
    }
  }, [showError]);

  useEffect(() => {
    if (activeTab === 'overrides') {
      fetchProjects();
    }
  }, [activeTab, fetchProjects]);

  const fetchOverrides = useCallback(async () => {
    if (!selectedProjectId) return;
    try {
      setLoadingOverrides(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (overrideDomain) params.append('domain', overrideDomain);
      if (overrideHidden) params.append('hidden', overrideHidden);
      const response = await fetch(getApiUrl(`status-overrides/${selectedProjectId}?${params}`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setOverrides(data.data || []);
      } else {
        showError(data.error || 'Erro ao carregar overrides');
      }
    } catch (error) {
      console.error('Erro ao buscar overrides:', error);
      showError('Erro ao carregar overrides');
    } finally {
      setLoadingOverrides(false);
    }
  }, [selectedProjectId, overrideDomain, overrideHidden, showError]);

  useEffect(() => {
    if (activeTab === 'overrides' && selectedProjectId) {
      fetchOverrides();
    }
  }, [activeTab, selectedProjectId, overrideDomain, overrideHidden, fetchOverrides]);

  const { hasPermission } = useAuth();
  useEffect(() => {
    // Usar permissões efetivas do backend; fallback para ADM/ADMIN
    const can = hasPermission('canManageProjects', ['ADM', 'ADMIN']) || hasPermission('canEditRole', ['ADM', 'ADMIN']);
    setCanManageStatus(!!can);
  }, [hasPermission]);

  const domains = [
    '',
    'company',
    'project',
    'milestone',
    'test_case',
    'test_run',
    'test_result',
    'exploratory_session',
    'automation_run',
    'github_sync',
    'user_approval'
  ];

  const openCreateModal = () => {
    if (!canManageStatus) {
      showError('Você não tem permissão para criar status.');
      return;
    }
    setEditingStatus(null);
    setModalTab('status');
    setFormData({
      domain: filterDomain || '',
      key: '',
      label: '',
      color: '#cccccc',
      icon: '',
      is_active: true,
      is_default: false,
      // flags de escopo
      scope_executions: true,
      scope_sessions: true,
      scope_automation: true
    });
    setFormErrors({});
    setSelectedProjectsForStatus([]);
    setProjectsAction('visible');
    setProjectOverrideOrder(0);
    // Garantir lista de projetos disponível para aba "Projetos" do modal
    fetchProjects();
    setShowModal(true);
  };

  const openEditModal = (status) => {
    if (!canManageStatus) {
      showError('Você não tem permissão para editar status.');
      return;
    }
    if (status.is_system) {
      showWarning('Status do sistema não pode ser editado.');
      return;
    }
    setEditingStatus(status);
    setModalTab('status');
    setFormData({
      domain: status.domain || '',
      key: status.key || '',
      label: status.label || '',
      color: status.color || '#cccccc',
      icon: status.icon || '',
      is_active: !!status.is_active,
      is_default: !!status.is_default,
      // flags de escopo
      scope_executions: typeof status.scope_executions !== 'undefined' ? !!status.scope_executions : true,
      scope_sessions: typeof status.scope_sessions !== 'undefined' ? !!status.scope_sessions : true,
      scope_automation: typeof status.scope_automation !== 'undefined' ? !!status.scope_automation : true
    });
    setFormErrors({});
    setSelectedProjectsForStatus([]);
    setProjectsAction('visible');
    setProjectOverrideOrder(0);
    // Garantir lista de projetos disponível para aba "Projetos" do modal
    fetchProjects();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingStatus(null);
    setFormErrors({});
    setSelectedProjectsForStatus([]);
    setProjectsAction('visible');
    setProjectOverrideOrder(0);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateForm = () => {
    const errors = {};
    const validDomains = domains.filter(d => !!d);
    const domain = (formData.domain || '').trim();
    const key = (formData.key || '').trim();
    const label = (formData.label || '').trim();
    const color = (formData.color || '').trim();
    if (!domain || !validDomains.includes(domain)) {
      errors.domain = 'Selecione um domínio válido.';
    }
    if (!key) {
      errors.key = 'A chave do status (key) é obrigatória.';
    }
    if (key && key.length > 100) {
      errors.key = 'A chave (key) deve ter no máximo 100 caracteres.';
    }
    if (!label) {
      errors.label = 'O rótulo do status (label) é obrigatório.';
    }
    if (label && label.length > 150) {
      errors.label = 'O rótulo (label) deve ter no máximo 150 caracteres.';
    }
    if (color && !/^#([A-Fa-f0-9]{6})$/.test(color)) {
      errors.color = 'Cor inválida. Use o formato HEX #RRGGBB.';
    }
    // Validação de escopos: ao menos um precisa estar marcado
    const scopeExecutions = !!formData.scope_executions;
    const scopeSessions = !!formData.scope_sessions;
    const scopeAutomation = !!formData.scope_automation;
    if (!scopeExecutions && !scopeSessions && !scopeAutomation) {
      errors.scope = 'Selecione ao menos um escopo (Execuções, Sessões ou Automação).';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canManageStatus) {
      showError('Você não tem permissão para salvar status.');
      return;
    }
    if (!validateForm()) return;

    try {
      const token = localStorage.getItem('token');
      const payload = {
        domain: formData.domain,
        key: formData.key.trim(),
        label: formData.label.trim(),
        color: formData.color || '#cccccc',
        icon: formData.icon || '',
        is_active: !!formData.is_active,
        is_default: !!formData.is_default,
        // incluir flags de escopo
        scope_executions: !!formData.scope_executions,
        scope_sessions: !!formData.scope_sessions,
        scope_automation: !!formData.scope_automation
      };

      let response;
      if (editingStatus) {
        response = await fetch(getApiUrl(`status/${editingStatus.id}`), {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
      } else {
        response = await fetch(getApiUrl('status'), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
      }

      const data = await response.json();
      if (response.ok && data.success) {
        const savedStatusId = editingStatus ? editingStatus.id : (data.data && data.data.id);
        if (selectedProjectsForStatus.length > 0 && savedStatusId) {
          const hiddenFlag = projectsAction === 'hidden';
          const orderVal = Number(projectOverrideOrder) || 0;
          const tokenLocal = localStorage.getItem('token');
          const requests = selectedProjectsForStatus.map(async (pid) => {
            const resp = await fetch(getApiUrl(`status-overrides/${pid}`), {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${tokenLocal}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ status_id: savedStatusId, hidden: hiddenFlag, order: orderVal })
            });
            const rdata = await resp.json();
            return resp.ok && rdata.success;
          });
          const results = await Promise.all(requests);
          const okCount = results.filter(Boolean).length;
          if (okCount === selectedProjectsForStatus.length) {
            showSuccess(editingStatus ? 'Status atualizado e projetos aplicados!' : 'Status criado e projetos aplicados!');
          } else if (okCount > 0) {
            showWarning('Status salvo, mas alguns projetos não foram aplicados.');
          } else {
            showWarning('Status salvo, porém aplicação em projetos falhou.');
          }
        } else {
          showSuccess(editingStatus ? 'Status atualizado com sucesso!' : 'Status criado com sucesso!');
        }
        closeModal();
        fetchStatuses();
      } else {
        showError(data.error || 'Erro ao salvar status');
      }
    } catch (error) {
      console.error('Erro ao salvar status:', error);
      showError('Erro ao salvar status');
    }
  };

  // Pré-preencher seleção de projetos no modal com base nos overrides existentes
  const prefillProjectsFromOverrides = useCallback(async () => {
    if (!editingStatus || !Array.isArray(projects) || projects.length === 0) return;
    try {
      setPrefillLoading(true);
      setPrefillError('');
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (editingStatus.domain) params.append('domain', editingStatus.domain);
      const requests = projects.map(async (p) => {
        try {
          const resp = await fetch(getApiUrl(`status-overrides/${p.id}?${params.toString()}`), {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const data = await resp.json();
          if (resp.ok && data.success) {
            const has = (data.data || []).some(ov => ov.status_id === editingStatus.id);
            return has ? p.id : false;
          }
          return false;
        } catch (err) {
          console.warn('Falha ao verificar overrides do projeto', p.id, err);
          return false;
        }
      });
      const results = await Promise.all(requests);
      const selectedIds = results.filter(Boolean);
      setSelectedProjectsForStatus(selectedIds);
    } catch (error) {
      console.error('Erro ao pré-preencher projetos:', error);
      setPrefillError('Erro ao pré-preencher projetos');
    } finally {
      setPrefillLoading(false);
    }
  }, [projects, editingStatus]);

  useEffect(() => {
    // Quando o modal estiver aberto na aba de projetos durante edição, realizar o pré-preenchimento
    if (showModal && modalTab === 'projects' && editingStatus) {
      // Se projetos ainda não carregados, tentar buscar
      if (projects.length === 0) {
        fetchProjects().then(() => {
          prefillProjectsFromOverrides();
        });
      } else {
        prefillProjectsFromOverrides();
      }
    }
  }, [showModal, modalTab, editingStatus, projects, fetchProjects, prefillProjectsFromOverrides]);

  const handleOverrideLocalChange = (id, field, value) => {
    setOverrides(prev => prev.map(ov => ov.id === id ? { ...ov, [field]: value } : ov));
  };

  const handleOverrideSave = async (override) => {
    if (!canManageStatus) {
      showError('Você não tem permissão para atualizar overrides.');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const payload = {
        status_id: override.status_id,
        hidden: !!override.hidden,
        order: Number(override.order) || 0
      };
      const response = await fetch(getApiUrl(`status-overrides/${selectedProjectId}`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (response.ok && data.success) {
        showSuccess('Override salvo com sucesso!');
        fetchOverrides();
      } else {
        showError(data.error || 'Erro ao salvar override');
      }
    } catch (error) {
      console.error('Erro ao salvar override:', error);
      showError('Erro ao salvar override');
    }
  };

  const handleOverrideDelete = async (override) => {
    if (!canManageStatus) {
      showError('Você não tem permissão para excluir overrides.');
      return;
    }
    // A exclusão será confirmada por modal; esta função executa a remoção
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`status-overrides/${selectedProjectId}/${override.id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        showSuccess('Override excluído com sucesso!');
        fetchOverrides();
      } else {
        showError(data.error || 'Erro ao excluir override');
      }
    } catch (error) {
      console.error('Erro ao excluir override:', error);
      showError('Erro ao excluir override');
    }
  };

  const availableStatusesForOverride = statuses.filter(s => {
    if (overrideDomain && s.domain !== overrideDomain) return false;
    const already = overrides.some(ov => ov.status_id === s.id);
    return !already;
  });

  const handleAddOverride = async () => {
    if (!canManageStatus) {
      showError('Você não tem permissão para criar overrides.');
      return;
    }
    if (!selectedProjectId) {
      showWarning('Selecione um projeto primeiro.');
      return;
    }
    if (!newOverrideStatusId) {
      showWarning('Selecione um status para adicionar override.');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const payload = {
        status_id: newOverrideStatusId,
        hidden: false,
        order: Number(newOverrideOrder) || 0
      };
      const response = await fetch(getApiUrl(`status-overrides/${selectedProjectId}`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (response.ok && data.success) {
        showSuccess('Override criado com sucesso!');
        setNewOverrideStatusId('');
        setNewOverrideOrder(0);
        fetchOverrides();
      } else {
        showError(data.error || 'Erro ao criar override');
      }
    } catch (error) {
      console.error('Erro ao criar override:', error);
      showError('Erro ao criar override');
    }
  };

  const openDeleteModal = (status) => {
    if (!canManageStatus) {
      showError('Você não tem permissão para excluir status.');
      return;
    }
    if (status.is_system) {
      showWarning('Status do sistema não pode ser excluído.');
      return;
    }
    setStatusToDelete(status);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setStatusToDelete(null);
  };

  const confirmDelete = async () => {
    if (!statusToDelete) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`status/${statusToDelete.id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        showSuccess('Status excluído com sucesso!');
        fetchStatuses();
        closeDeleteModal();
      } else {
        showError(data.error || 'Erro ao excluir status');
      }
    } catch (error) {
      console.error('Erro ao excluir status:', error);
      showError('Erro ao excluir status');
    } finally {
      closeDeleteModal();
    }
  };

  const handleDelete = (status) => {
    openDeleteModal(status);
  };

  return (
    <div className="status-container">
      <div className="status-header">
        <div className="header-left">
          <button 
            className="btn-back"
            onClick={() => navigate('/overview')}
            title="Voltar ao Menu Principal"
          >
            ← Voltar ao Menu
          </button>
          <div>
            <h1>🏷️ Status</h1>
            <p>Listagem dos status disponíveis no sistema</p>
          </div>
        </div>
        {canManageStatus && (
          <button 
            className="btn-primary"
            onClick={openCreateModal}
          >
            ➕ Novo Status
          </button>
        )}
      </div>

      <div className="tabs">
        <button
          className={`tab-button ${activeTab === 'definitions' ? 'active' : ''}`}
          onClick={() => setActiveTab('definitions')}
        >
          Definições
        </button>
        <button
          className={`tab-button ${activeTab === 'overrides' ? 'active' : ''}`}
          onClick={() => setActiveTab('overrides')}
        >
          Overrides
        </button>
      </div>

      {activeTab === 'definitions' && (
        <div className="status-filters">
          <select 
            value={filterDomain}
            onChange={(e) => setFilterDomain(e.target.value)}
            className="filter-input"
          >
            {domains.map(d => (
              <option key={d} value={d}>{d ? d : 'Todos os domínios'}</option>
            ))}
          </select>
          <select 
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value)}
            className="filter-input"
          >
            <option value="">Todos</option>
            <option value="true">Ativos</option>
            <option value="false">Inativos</option>
          </select>
        </div>
      )}

      {activeTab === 'definitions' && (
        loading ? (
          <div className="loading">Carregando status...</div>
        ) : (
          <div className="status-list">
            {statuses.length === 0 ? (
              <div className="empty-state">Nenhum status encontrado</div>
            ) : (
              statuses.map(status => (
                <div key={status.id} className="status-item">
                  <div className="status-left">
                    <span className="status-color" style={{ backgroundColor: status.color || '#ccc' }} />
                    <div>
                      <div className="status-label">{status.label}</div>
                      <div className="status-meta">
                        <span className="status-key">{status.key}</span>
                        <span className="status-domain">{status.domain}</span>
                        {status.is_system && <span className="status-tag">system</span>}
                        {status.is_default && <span className="status-tag">default</span>}
                      </div>
                    </div>
                  </div>
                  <div className="status-right">
                    <span className={status.is_active ? 'active-badge' : 'inactive-badge'}>
                      {status.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                    {canManageStatus && (
                      <div className="status-actions">
                        <button 
                          className="btn-secondary"
                          title={status.is_system ? 'Status do sistema não pode ser editado' : 'Editar Status'}
                          disabled={!!status.is_system}
                          onClick={() => openEditModal(status)}
                        >
                          ✏️ Editar
                        </button>
                        <button 
                          className="btn-danger"
                          title={status.is_system ? 'Status do sistema não pode ser excluído' : 'Excluir Status'}
                          disabled={!!status.is_system}
                          onClick={() => handleDelete(status)}
                        >
                          🗑️ Excluir
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )
      )}

      {activeTab === 'overrides' && (
        <div className="overrides-container">
          <div className="status-filters">
            <select 
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="filter-input"
            >
              <option value="">Selecione um projeto</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name || p.project_name || p.id}</option>
              ))}
            </select>
            <select 
              value={overrideDomain}
              onChange={(e) => setOverrideDomain(e.target.value)}
              className="filter-input"
            >
              {domains.map(d => (
                <option key={d} value={d}>{d ? d : 'Todos os domínios'}</option>
              ))}
            </select>
            <select 
              value={overrideHidden}
              onChange={(e) => setOverrideHidden(e.target.value)}
              className="filter-input"
            >
              <option value="">Todos</option>
              <option value="true">Ocultos</option>
              <option value="false">Visíveis</option>
            </select>
          </div>

          {loadingOverrides ? (
            <div className="loading">Carregando overrides...</div>
          ) : (
            <div>
              {!selectedProjectId ? (
                <div className="empty-state">Selecione um projeto para visualizar overrides.</div>
              ) : (
                <>
                  <div className="override-row" style={{ marginBottom: 12 }}>
                    <div style={{ flex: 1 }}>
                      <label>Status para adicionar override</label>
                      <select
                        value={newOverrideStatusId}
                        onChange={(e) => setNewOverrideStatusId(e.target.value)}
                      >
                        <option value="">Selecione...</option>
                        {availableStatusesForOverride.map(s => (
                          <option key={s.id} value={s.id}>{`${s.domain} :: ${s.label}`}</option>
                        ))}
                      </select>
                    </div>
                    <div className="override-controls">
                      <label>Ordem</label>
                      <input
                        type="number"
                        className="order-input"
                        value={newOverrideOrder}
                        onChange={(e) => setNewOverrideOrder(Number(e.target.value))}
                      />
                      <button className="btn-primary" onClick={handleAddOverride}>Adicionar</button>
                    </div>
                  </div>

                  {overrides.length === 0 ? (
                    <div className="empty-state">Nenhum override encontrado para este projeto</div>
                  ) : (
                    overrides.map(ov => (
                      <div key={ov.id} className="override-row">
                        <div className="status-left">
                          <span className="status-color" style={{ backgroundColor: ov.color || '#ccc' }} />
                          <div>
                            <div className="status-label">{ov.label}</div>
                            <div className="status-meta">
                              <span className="status-key">{ov.key}</span>
                              <span className="status-domain">{ov.domain}</span>
                              {ov.is_default && <span className="status-tag">default</span>}
                            </div>
                          </div>
                        </div>
                        <div className="override-controls">
                          <label className="checkbox">
                            <input
                              type="checkbox"
                              checked={!!ov.hidden}
                              onChange={(e) => handleOverrideLocalChange(ov.id, 'hidden', e.target.checked)}
                            />
                            Oculto
                          </label>
                          <label>Ordem</label>
                          <input
                            type="number"
                            className="order-input"
                            value={ov.order || 0}
                            onChange={(e) => handleOverrideLocalChange(ov.id, 'order', Number(e.target.value))}
                          />
                          {canManageStatus && (
                            <>
                              <button className="btn-secondary" onClick={() => handleOverrideSave(ov)}>Salvar</button>
                              <button className="btn-danger" onClick={() => { setOverrideToDelete(ov); setShowDeleteOverrideModal(true); }}>Excluir</button>
                            </>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingStatus ? '✏️ Editar Status' : '➕ Novo Status'}</h2>
              <button className="btn-close" onClick={closeModal}>✕</button>
            </div>
            <form onSubmit={handleSubmit} className="status-form">
            <div className="tabs" style={{ marginBottom: 12 }}>
              <button type="button" className={`tab-button ${modalTab === 'status' ? 'active' : ''}`} onClick={() => setModalTab('status')}>Definições</button>
              <button type="button" className={`tab-button ${modalTab === 'projects' ? 'active' : ''}`} onClick={() => setModalTab('projects')}>Projetos</button>
              <button type="button" className={`tab-button ${modalTab === 'scope' ? 'active' : ''}`} onClick={() => setModalTab('scope')}>Escopo</button>
            </div>

              {modalTab === 'status' && (
                <div>
                  <div className="form-group">
                    <label htmlFor="domain">Domínio</label>
                    <select 
                      id="domain"
                      name="domain"
                      value={formData.domain}
                      onChange={handleFormChange}
                    >
                      <option value="">Selecione...</option>
                      {domains.filter(d => !!d).map(d => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                    {formErrors.domain && <div className="form-error">{formErrors.domain}</div>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="key">Chave (key)</label>
                    <input 
                      id="key"
                      name="key"
                      type="text"
                      value={formData.key}
                      onChange={handleFormChange}
                      maxLength={100}
                      onKeyDown={(e) => {
                        const allowed = [
                          'Backspace', 'Delete', 'Tab', 'Escape',
                          'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
                          'Home', 'End', 'Enter',
                          'Control', 'Shift', 'Alt', 'Meta', 'CapsLock', 'Dead'
                        ];
                        if (allowed.includes(e.key)) return;
                        if (e.key && e.key.length === 1) {
                          const el = e.target;
                          const cur = el.value || '';
                          const selStart = el.selectionStart ?? cur.length;
                          const selEnd = el.selectionEnd ?? cur.length;
                          const willLen = cur.length - (selEnd - selStart) + 1; // 1 char
                          if (willLen > 100) e.preventDefault();
                        }
                      }}
                      onBeforeInput={(e) => {
                        if ((e.inputType && e.inputType.startsWith('delete')) || e.data == null) return;
                        const el = e.target;
                        const cur = el.value || '';
                        const selStart = el.selectionStart ?? cur.length;
                        const selEnd = el.selectionEnd ?? cur.length;
                        const dataLen = e.data ? e.data.length : 0;
                        const willLen = cur.length - (selEnd - selStart) + dataLen;
                        if (willLen > 100) e.preventDefault();
                      }}
                      onPaste={(e) => {
                        const text = e.clipboardData.getData('text');
                        const el = e.target;
                        const cur = el.value || '';
                        const selStart = el.selectionStart ?? cur.length;
                        const selEnd = el.selectionEnd ?? cur.length;
                        const willLen = cur.length - (selEnd - selStart) + text.length;
                        if (willLen > 100) e.preventDefault();
                      }}
                      onDrop={(e) => e.preventDefault()}
                      placeholder="ex: active, completed"
                    />
                    <div
                      className={(formData.key?.length ?? 0) >= 100 ? 'char-counter limit' : 'char-counter'}
                      aria-live="polite"
                    >
                      {(formData.key?.length ?? 0)}/100
                    </div>
                    {formErrors.key && <div className="form-error">{formErrors.key}</div>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="label">Rótulo (label)</label>
                    <input 
                      id="label"
                      name="label"
                      type="text"
                      value={formData.label}
                      onChange={handleFormChange}
                      maxLength={150}
                      onKeyDown={(e) => {
                        const allowed = [
                          'Backspace', 'Delete', 'Tab', 'Escape',
                          'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
                          'Home', 'End', 'Enter',
                          'Control', 'Shift', 'Alt', 'Meta', 'CapsLock', 'Dead'
                        ];
                        if (allowed.includes(e.key)) return;
                        if (e.key && e.key.length === 1) {
                          const el = e.target;
                          const cur = el.value || '';
                          const selStart = el.selectionStart ?? cur.length;
                          const selEnd = el.selectionEnd ?? cur.length;
                          const willLen = cur.length - (selEnd - selStart) + 1; // 1 char
                          if (willLen > 150) e.preventDefault();
                        }
                      }}
                      onBeforeInput={(e) => {
                        if ((e.inputType && e.inputType.startsWith('delete')) || e.data == null) return;
                        const el = e.target;
                        const cur = el.value || '';
                        const selStart = el.selectionStart ?? cur.length;
                        const selEnd = el.selectionEnd ?? cur.length;
                        const dataLen = e.data ? e.data.length : 0;
                        const willLen = cur.length - (selEnd - selStart) + dataLen;
                        if (willLen > 150) e.preventDefault();
                      }}
                      onPaste={(e) => {
                        const text = e.clipboardData.getData('text');
                        const el = e.target;
                        const cur = el.value || '';
                        const selStart = el.selectionStart ?? cur.length;
                        const selEnd = el.selectionEnd ?? cur.length;
                        const willLen = cur.length - (selEnd - selStart) + text.length;
                        if (willLen > 150) e.preventDefault();
                      }}
                      onDrop={(e) => e.preventDefault()}
                    />
                    <div
                      className={(formData.label?.length ?? 0) >= 150 ? 'char-counter limit' : 'char-counter'}
                      aria-live="polite"
                    >
                      {(formData.label?.length ?? 0)}/150
                    </div>
                    {formErrors.label && <div className="form-error">{formErrors.label}</div>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="color">Cor</label>
                    <input 
                      id="color"
                      name="color"
                      type="color"
                      value={formData.color}
                      onChange={handleFormChange}
                    />
                    {formErrors.color && <div className="form-error">{formErrors.color}</div>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="icon">Ícone (opcional)</label>
                    <input 
                      id="icon"
                      name="icon"
                      type="text"
                      value={formData.icon}
                      onChange={handleFormChange}
                      placeholder="ex: ✅, 🚀"
                    />
                  </div>

                  <div className="form-row">
                    <label className="checkbox">
                      <input 
                        type="checkbox"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleFormChange}
                      />
                      Ativo
                    </label>
                    <label className="checkbox">
                      <input 
                        type="checkbox"
                        name="is_default"
                        checked={formData.is_default}
                        onChange={handleFormChange}
                      />
                      Padrão do domínio
                    </label>
                  </div>
                </div>
              )}

              {modalTab === 'scope' && (
                <div>
                  <div className="form-group">
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                      Defina onde este status é aplicável.
                    </div>
                    <div className="form-row">
                      <label className="checkbox">
                        <input
                          type="checkbox"
                          name="scope_executions"
                          checked={!!formData.scope_executions}
                          onChange={handleFormChange}
                        />
                        Execuções
                      </label>
                      <label className="checkbox">
                        <input
                          type="checkbox"
                          name="scope_sessions"
                          checked={!!formData.scope_sessions}
                          onChange={handleFormChange}
                        />
                        Sessões
                      </label>
                      <label className="checkbox">
                        <input
                          type="checkbox"
                          name="scope_automation"
                          checked={!!formData.scope_automation}
                          onChange={handleFormChange}
                        />
                        Automação
                      </label>
                    </div>
                    {formErrors.scope && (
                      <div className="form-error" style={{ marginTop: 8 }}>{formErrors.scope}</div>
                    )}
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>
                      Dica: marque pelo menos um escopo para que o status seja utilizável.
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8, lineHeight: 1.5 }}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>O que significa cada escopo?</div>
                      <div>• Execuções: usado em casos de teste manuais.</div>
                      <div>• Sessões: usado em sessões exploratórias.</div>
                      <div>• Automação: usado em integrações com ferramentas automatizadas.</div>
                      <div style={{ marginTop: 6 }}>Se nenhum escopo estiver habilitado, o status não pode ser utilizado em nenhum fluxo.</div>
                      <div>Alterações de escopo refletem imediatamente em formulários e execuções.</div>
                    </div>
                  </div>
                </div>
              )}

              {modalTab === 'projects' && (
                <div>
                  <div className="form-group">
                    <label>Selecione projetos</label>
                    {editingStatus && (
                      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                        {prefillLoading ? (
                          'Carregando seleção de projetos...'
                        ) : prefillError ? (
                          <span style={{ color: '#dc2626' }}>{prefillError}</span>
                        ) : selectedProjectsForStatus.length > 0 ? (
                          'Projetos com override deste status foram pré-selecionados.'
                        ) : (
                          'Nenhum projeto possui override para este status.'
                        )}
                      </div>
                    )}
                    <div className="projects-actions">
                      <input
                        className="search-input"
                        placeholder="Buscar projetos..."
                        value={projectsSearch}
                        onChange={(e) => setProjectsSearch(e.target.value)}
                      />
                      <div className="list-actions">
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() => setSelectedProjectsForStatus(projects.map(p => p.id))}
                        >Marcar todos</button>
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() => setSelectedProjectsForStatus([])}
                        >Limpar</button>
                        <span className="selected-count">{selectedProjectsForStatus.length} selecionado(s)</span>
                      </div>
                    </div>

                    <div className="projects-list">
                      {projects.length === 0 ? (
                        <div className="empty-state">Nenhum projeto disponível.</div>
                      ) : (
                        projects
                          .filter(p => {
                            const term = projectsSearch.trim().toLowerCase();
                            if (!term) return true;
                            const name = (p.name || '').toLowerCase();
                            const idStr = String(p.id);
                            return name.includes(term) || idStr.includes(term);
                          })
                          .map(p => {
                            const isSelected = selectedProjectsForStatus.includes(p.id);
                            return (
                              <label key={p.id} className={`project-item ${isSelected ? 'selected' : ''}`}>
                                <span className="project-name">{p.name || 'Projeto'}</span>
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setSelectedProjectsForStatus(prev => {
                                      if (checked) return [...prev, p.id];
                                      return prev.filter(id => id !== p.id);
                                    });
                                  }}
                                />
                              </label>
                            );
                          })
                      )}
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>Ação</label>
                      <select value={projectsAction} onChange={(e) => setProjectsAction(e.target.value)}>
                        <option value="visible">Tornar visível nos selecionados</option>
                        <option value="hidden">Ocultar nos selecionados</option>
                      </select>
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>Ordem (opcional)</label>
                      <input type="number" value={projectOverrideOrder} onChange={(e) => setProjectOverrideOrder(Number(e.target.value))} />
                    </div>
                  </div>
                  <div className="form-error" style={{ display: selectedProjectsForStatus.length === 0 ? 'block' : 'none' }}>Selecione ao menos um projeto para aplicar.</div>
                </div>
              )}

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={closeModal}>Cancelar</button>
                <button type="submit" className="btn-primary">{editingStatus ? 'Salvar' : 'Criar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de confirmação para excluir override */}
      {showDeleteOverrideModal && overrideToDelete && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Confirmar Exclusão</h2>
            </div>
            <div className="modal-content">
              <p>
                Tem certeza que deseja excluir o override do status "{overrideToDelete.label}"?
              </p>
            </div>
            <div className="modal-actions">
              <button
                className="btn-danger"
                onClick={() => { setShowDeleteOverrideModal(false); setOverrideToDelete(null); }}
              >
                Cancelar
              </button>
              <button
                className="btn-danger"
                onClick={async () => {
                  const ov = overrideToDelete;
                  setShowDeleteOverrideModal(false);
                  setOverrideToDelete(null);
                  await handleOverrideDelete(ov);
                }}
              >
                Excluir Override
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && statusToDelete && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirmar Exclusão</h2>
              <button className="close-btn" onClick={closeDeleteModal}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{
                backgroundColor: '#fee2e2',
                border: '1px solid #fca5a5',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '20px'
              }}>
                <p style={{ color: '#991b1b', margin: 0, fontWeight: '500' }}>
                  ⚠️ Ação irreversível
                </p>
              </div>
              <p>
                Tem certeza que deseja deletar o status <strong>"{statusToDelete.label}"</strong>?
              </p>
              <p style={{ color: '#666', fontSize: '0.9em' }}>
                Esta ação não pode ser desfeita.
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-primary"
                onClick={closeDeleteModal}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmDelete}
              >
                🗑️ Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Status;